import { Head, Link } from "@inertiajs/react"

import Navbar from "@/components/ttt/Navbar"
import Footer from "@/components/ttt/Footer"
import ProductCard from "@/components/ttt/ProductCard"
import type {
  TttChrome,
  TttAuthorDetail,
  TttCardItem,
  TttCardI18n,
} from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  author: TttAuthorDetail
  books: TttCardItem[]
  holiday: boolean
  i18n: TttCardI18n
  labels: {
    site_perso: string
    books_intro: string
    back: string
  }
}

// Faithful port of app/views/ttt/authors/show.html.slim: avatar, bio, website
// button and the author's purchasable books rendered as TTT product cards.
export default function Author({
  chrome,
  author,
  books,
  holiday,
  i18n,
  labels,
}: Props) {
  return (
    <>
      <Head>
        <title>{`${author.nickname} — TTT`}</title>
      </Head>

      <Navbar chrome={chrome} />

      <div className="container mx-auto px-4 py-8 pt-36">
        <div className="max-w-4xl mx-auto">
          <div className="bg-light border border-primary rounded-lg p-6 shadow-lg">
            <div className="flex flex-col items-center gap-y-4 mb-6">
              <div className="border border-primary rounded-lg overflow-hidden">
                {author.avatar_url ? (
                  <img
                    src={author.avatar_url}
                    alt={author.nickname}
                    className="w-52 h-52 object-cover"
                  />
                ) : (
                  <div className="w-52 h-52 bg-light flex items-center justify-center text-primary">
                    <i className="fa-solid fa-user text-5xl" />
                  </div>
                )}
              </div>

              <h1 className="text-2xl font-bold text-primary uppercase">
                {author.nickname}
              </h1>

              {author.bio && (
                <p className="text-base text-center text-primary">{author.bio}</p>
              )}

              {author.website && (
                <a
                  href={author.website}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                >
                  {labels.site_perso}
                </a>
              )}
            </div>

            {books.length > 0 && (
              <>
                <h2 className="text-primary text-lg font-semibold mb-4">
                  {labels.books_intro}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                  {books.map((item) => (
                    <ProductCard
                      key={item.id}
                      item={item}
                      backpackSrc={chrome.images.backpack}
                      holiday={holiday}
                      i18n={i18n}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="mt-6">
              <Link
                href={chrome.paths.authors}
                className="text-primary hover:underline"
              >
                {labels.back}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer chrome={chrome} />
    </>
  )
}
